import type { IntlShape } from 'react-intl';

/** Columns of the "Top screens" list, in display order. */
export type TopScreensColumnId =
  | 'zone'
  | 'publisher'
  | 'venueType'
  | 'poi'
  | 'ota'
  | 'impressions'
  | 'playouts'
  | 'onTarget'
  | 'screenFormat'
  | 'resolution'
  | 'spotLength'
  | 'estimatedAudience'
  | 'actions';

type TopScreensColumn = {
  id: TopScreensColumnId;
  /** Grid track(s) of the column; "Screens" also takes the rank badge track. */
  width: string;
  /** Listed in the "Columns" menu. Screens and the row actions are always shown. */
  hideable: boolean;
  /** Shown until the user saves a selection of their own. */
  visibleByDefault: boolean;
};

export const TOP_SCREENS_LIST_ID = 'doohReport.topScreens';

export const TOP_SCREENS_COLUMNS: TopScreensColumn[] = [
  { hideable: false, id: 'zone', visibleByDefault: true, width: '40px minmax(0, 3.2fr)' },
  { hideable: true, id: 'publisher', visibleByDefault: true, width: 'minmax(0, 0.9fr)' },
  { hideable: true, id: 'venueType', visibleByDefault: true, width: 'minmax(0, 0.9fr)' },
  { hideable: true, id: 'poi', visibleByDefault: true, width: 'minmax(0, 1.3fr)' },
  { hideable: true, id: 'ota', visibleByDefault: true, width: 'minmax(0, 0.6fr)' },
  { hideable: true, id: 'impressions', visibleByDefault: true, width: 'minmax(0, 0.8fr)' },
  { hideable: true, id: 'playouts', visibleByDefault: true, width: 'minmax(0, 0.7fr)' },
  { hideable: true, id: 'onTarget', visibleByDefault: true, width: 'minmax(0, 0.8fr)' },
  { hideable: true, id: 'screenFormat', visibleByDefault: false, width: 'minmax(0, 0.9fr)' },
  { hideable: true, id: 'resolution', visibleByDefault: false, width: 'minmax(0, 0.8fr)' },
  { hideable: true, id: 'spotLength', visibleByDefault: false, width: 'minmax(0, 0.6fr)' },
  { hideable: true, id: 'estimatedAudience', visibleByDefault: false, width: 'minmax(0, 0.8fr)' },
  { hideable: false, id: 'actions', visibleByDefault: true, width: '64px' }
];

export const TOP_SCREENS_HIDEABLE_COLUMN_IDS = TOP_SCREENS_COLUMNS.filter(({ hideable }) => hideable).map(
  ({ id }) => id
);

export const TOP_SCREENS_DEFAULT_HIDDEN_COLUMN_IDS = TOP_SCREENS_COLUMNS.filter(
  ({ visibleByDefault }) => !visibleByDefault
).map(({ id }) => id);

/** `grid-template-columns` of the list for the columns currently shown. */
export function buildTopScreensGridColumns(isVisible: (columnId: TopScreensColumnId) => boolean): string {
  return TOP_SCREENS_COLUMNS.filter(({ hideable, id }) => !hideable || isVisible(id))
    .map(({ width }) => width)
    .join(' ');
}

/** Header label of every column but the row actions. */
export function getTopScreensColumnLabels(
  formatMessage: IntlShape['formatMessage']
): Record<Exclude<TopScreensColumnId, 'actions'>, string> {
  return {
    estimatedAudience: formatMessage({
      defaultMessage: 'Estimated audience',
      id: 'doohReport.deliveryDrivers.column.estimatedAudience'
    }),
    impressions: formatMessage({ defaultMessage: 'Impressions', id: 'doohReport.deliveryDrivers.column.impressions' }),
    onTarget: formatMessage({ defaultMessage: '% On-Target', id: 'doohReport.deliveryDrivers.column.onTarget.label' }),
    ota: formatMessage({ defaultMessage: 'OTA', id: 'doohReport.deliveryDrivers.column.ota' }),
    playouts: formatMessage({ defaultMessage: 'Playouts', id: 'doohReport.deliveryDrivers.column.playouts' }),
    poi: formatMessage({ defaultMessage: 'Nearby Affinity POI', id: 'doohReport.deliveryDrivers.column.poi' }),
    publisher: formatMessage({ defaultMessage: 'Publisher', id: 'doohReport.deliveryDrivers.column.publisher' }),
    resolution: formatMessage({ defaultMessage: 'Resolution', id: 'doohReport.deliveryDrivers.column.resolution' }),
    screenFormat: formatMessage({
      defaultMessage: 'Screen format',
      id: 'doohReport.deliveryDrivers.column.screenFormat'
    }),
    spotLength: formatMessage({ defaultMessage: 'Spot length', id: 'doohReport.deliveryDrivers.column.spotLength' }),
    venueType: formatMessage({ defaultMessage: 'Venue Type', id: 'doohReport.deliveryDrivers.column.venueType' }),
    zone: formatMessage({ defaultMessage: 'Screens', id: 'doohReport.deliveryDrivers.column.zone' })
  };
}
